import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Check, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ReadingExercisePage = () => {
  const { readingId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [reading, setReading] = useState(null);
  const [loading, setLoading] = useState(true);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [score, setScore] = useState(0);

  useEffect(() => {
    fetchReading();
  }, [readingId]);

  const fetchReading = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/reading/${readingId}`);
      
      if (response.status === 403) {
        toast.error('This is a premium exercise. Please upgrade to access.');
        navigate('/subscription');
        return;
      }
      
      if (response.ok) {
        const data = await response.json();
        setReading(data);
      }
    } catch (error) {
      console.error('Failed to fetch reading:', error);
      toast.error('Failed to load reading exercise');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = () => {
    let correctCount = 0;
    reading.questions.forEach((q, index) => {
      if (answers[index] === q.correct_answer) {
        correctCount++;
      }
    });
    setScore(correctCount);
    setSubmitted(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-babyblue-50 via-babyblue-100/50 to-white flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!reading) {
    return (
      <div className="min-h-screen flex flex-col bg-gradient-to-b from-babyblue-50 via-babyblue-100/50 to-white">
        <Navbar />
        <main className="flex-1 flex items-center justify-center px-6">
          <div className="text-center max-w-md">
            <h1 className="font-heading text-3xl font-bold text-babyblue-900 mb-3">
              Reading exercise could not be loaded
            </h1>
            <p className="text-babyblue-600 mb-6">
              Something went wrong loading this exercise. Please try again.
            </p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={() => { setLoading(true); fetchReading(); }}
                className="btn-primary"
                data-testid="retry-reading"
              >
                Retry
              </button>
              <Link to="/reading" className="btn-secondary">
                Back to Reading
              </Link>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-babyblue-50 via-babyblue-100/50 to-white flex flex-col">
      <Navbar />

      <main className="flex-1 py-8 px-6">
        <div className="content-focus">
          <div className="mb-8">
            <Link to="/reading" className="text-primary hover:text-babyblue-800 mb-4 inline-block transition-colors">
              ← Back to Reading Exercises
            </Link>
            <span className="bg-primary/20 text-primary px-4 py-2 rounded-full text-sm font-medium mb-4 inline-block ml-3">{reading.level}</span>
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-babyblue-800">{reading.title}</h1>
          </div>

          <div className="card p-8 mb-8">
            <div className="prose max-w-none">
              {reading.text.split('\n\n').map((paragraph, index) => (
                <p key={index} className="text-babyblue-900 leading-relaxed mb-4 text-lg">{paragraph}</p>
              ))}
            </div>
          </div>

          <div className="card p-8">
            <h2 className="font-heading text-2xl font-semibold text-babyblue-900 mb-6">Comprehension Questions</h2>
            
            {reading.questions.map((question, index) => (
              <div key={index} className="mb-6 pb-6 border-b border-babyblue-200 last:border-0">
                <p className="text-lg text-babyblue-900 mb-4 font-medium">{index + 1}. {question.question}</p>
                
                {question.type === 'multiple_choice' && (
                  <div className="space-y-3">
                    {question.options.map((option, optIndex) => {
                      const isSelected = answers[index] === option;
                      const isCorrect = option === question.correct_answer;
                      const showResult = submitted;
                      
                      return (
                        <button
                          key={optIndex}
                          onClick={() => !submitted && setAnswers({...answers, [index]: option})}
                          disabled={submitted}
                          className={`w-full text-left p-5 rounded-xl border-2 transition-all transform hover:scale-[1.02] ${
                            !showResult && isSelected ? 'border-primary bg-babyblue-100 shadow-lg shadow-primary/30' :
                            !showResult ? 'border-primary/40 bg-white hover:border-primary/60 hover:bg-babyblue-50' :
                            isCorrect ? 'border-green-500 bg-green-50 shadow-lg shadow-green-500/30' :
                            isSelected && !isCorrect ? 'border-red-500 bg-red-50 shadow-lg shadow-red-500/30' :
                            'border-babyblue-200 bg-white'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-babyblue-900 font-semibold text-lg">{option}</span>
                            {showResult && isCorrect && <Check className="w-6 h-6 text-green-600" />}
                            {showResult && isSelected && !isCorrect && <X className="w-6 h-6 text-red-600" />}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}

            {!submitted ? (
              <button
                onClick={handleSubmit}
                className="btn-primary mt-4"
                disabled={Object.keys(answers).length < reading.questions.length}
              >
                Submit Answers
              </button>
            ) : (
              <div className="mt-6 p-6 bg-babyblue-50 border-2 border-primary/50 rounded-lg">
                <h3 className="font-heading text-xl font-semibold text-babyblue-900 mb-2">Results</h3>
                <p className="text-lg text-babyblue-700">
                  You got <strong className="text-primary">{score}</strong> out of <strong className="text-primary">{reading.questions.length}</strong> correct!
                </p>
                <p className="text-sm text-babyblue-600 mt-2">
                  Score: {Math.round((score / reading.questions.length) * 100)}%
                </p>
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ReadingExercisePage;
